import { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Input, Spin } from 'antd';
import UserAvatar from '../common/UserAvatar';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { MEMBER_ROLE_OPTIONS } from '../../utils/constants';
import useAppMessage from '../../hooks/useAppMessage';
import teamService from '../../services/teamService';
import userService from '../../services/userService';
import MemberCard from '../card/MemberCard';
import ExploreCard from '../explore/ExploreCard';
import EmptyState from '../common/EmptyState';
import { useNavigate } from 'react-router-dom';

// Helper to map API content to card props
const mapContentToCard = (content) => {
  const fields = content.fields || {};
  
  // Handle author info
  let authorName = 'Unknown';
  let authorAvatar = '';
  
  if (content.user) {
    authorName = content.user.name || content.user.nickname || 'Unknown';
    authorAvatar = content.user.avatar || '';
  } else if (content.creator) {
    // Fallback if user object is not directly available but creator is
    authorName = content.creator.name || content.creator.nickname || 'Unknown';
    authorAvatar = content.creator.avatar || '';
  }
  
  // Process cover image URL
  let coverImage = null;
  if (fields.cover_image) {
    coverImage = fields.cover_image.startsWith('http') 
      ? fields.cover_image 
      : `/api/v2/contents/${fields.cover_image}/file`;
  }

  return {
    id: content.id,
    title: fields.title || fields.prompt || '无标题',
    description: fields.body_text || fields.response || fields.description || '',
    coverImage: coverImage,
    author: {
      name: authorName,
      avatar: authorAvatar
    },
    stats: {
      likes: content.interactions?.like_count || 0,
      is_liked: content.is_liked
    },
    tags: fields.tags || [],
    status: fields.status || content.status
  };
};

export default function WorkspaceTab({
  userId,
  isCaptain = false,
}) {
  const navigate = useNavigate();
  const { message, contextHolder } = useAppMessage();
  const stableMessageRef = useRef(message);
  stableMessageRef.current = message;
  const searchTimeout = useRef(null);

  const [activeTab, setActiveTab] = useState('members');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [localMembers, setLocalMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  
  // 用户搜索相关状态
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // 提案数据状态
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [proposalsPagination, setProposalsPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0
  });



  // 获取成员列表的函数
  const fetchMembers = useCallback(async () => {
    if (!userId) return;
    
    setMembersLoading(true);
    try {
      const response = await teamService.getTeamMembers(userId);
      const membersData = response || [];
      
      const formattedMembers = membersData.map(member => ({
        id: member.id,
        user_id: member.id, // 新数据结构中，id 就是 user_id
        name: member.username || '未知成员',
        avatar: member.avatar,
        bio: member.title || '',
        role: member.role,
        like_count: 0, // 新数据结构中没有 like_count 字段
        joined_at: member.inserted_at,
        status: member.member_status || 'confirmed', // 使用新的 member_status 字段
        is_invitation: member.member_status === 'invited', // 根据 member_status 判断是否为邀请成员
        invitation_id: member.invitation_id || null,
        invited_at: member.invited_at || null,
        expires_at: null,
        // 新增字段
        email: member.email,
        phone: member.phone,
        preferences: member.preferences || {},
        updated_at: member.updated_at,
        member_status: member.member_status // 保留原始字段
      }));
      
      setLocalMembers(formattedMembers);
    } catch (error) {
      console.error('获取成员列表失败:', error);
      stableMessageRef.current.error('获取成员列表失败，请重试');
    } finally {
      setMembersLoading(false);
    }
  }, [userId]);

  // 获取提案数据
  const fetchProposals = useCallback(async () => {
    if (!userId) return;
    
    setProposalsLoading(true);
    try {
      const res = await teamService.getTeamProposals(userId, {
        page: 1,
        per_page: 10
      });
      
      // 转换数据格式以匹配 ChallengeCard 期望的格式
      const transformedProposals = (res.contents || []).map((item, index) => ({
        ...item,
        // 将 fields 中的数据提取到顶层，以匹配 ChallengeCard 的期望格式
        prompt: item.fields?.title || '',
        response: item.fields?.body_text || '',
        topics: item.fields?.tags || [],
        colorIdx: index % 7, // 添加颜色索引
        // 保留原始的 fields 数据
        fields: {
          ...item.fields,
          // 确保 ChallengeCard 能从 fields 中正确解构
          title: item.fields?.title || '',
          body_text: item.fields?.body_text || '',
          tags: item.fields?.tags || [],
        }
      }));
      
      setProposals(transformedProposals);
      setProposalsPagination(res.pagination || {
        page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0
      });
    } catch (error) {
      console.error('获取提案数据失败:', error);
      setProposals([]);
      setProposalsPagination({
        page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0
      });
    } finally {
      setProposalsLoading(false);
    }
  }, [userId]);

  // 监听tab切换
  useEffect(() => {
    if (activeTab === 'members' && userId) {
      fetchMembers();
    } else if (activeTab === 'proposals' && userId) {
      fetchProposals();
    }
  }, [activeTab, userId, fetchMembers, fetchProposals]);

  // 搜索用户
  const handleSearchUsers = useCallback(async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await userService.searchUsers({
        name: keyword.trim(),
        limit: 10
      });
      setSearchResults(response.users || []);
    } catch (error) {
      console.error('搜索用户失败:', error);
      stableMessageRef.current.error('搜索用户失败，请重试');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // 处理搜索关键词变化
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchKeyword(value);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      handleSearchUsers(value);
    }, 500);
  }, [handleSearchUsers]);

  // 选择用户
  const handleSelectUser = useCallback((user) => {
    setSelectedUser(user);
    setSearchKeyword(user.name);
    setSearchResults([]);
  }, []);

  // 发送邀请
  const handleSendInvitation = useCallback(async () => {
    if (!selectedUser) {
      stableMessageRef.current.warning('请先选择要邀请的用户');
      return;
    }

    if (!inviteMessage.trim()) {
      stableMessageRef.current.warning('请输入邀请消息');
      return;
    }

    setInviting(true);
    try {
      await teamService.sendInvitation(userId, {
        invitee_id: selectedUser.id,
        message: inviteMessage.trim()
      });

      stableMessageRef.current.success('邀请已发送');
      setAddModalVisible(false);
      setSelectedUser(null);
      setSearchKeyword('');
      setInviteRole('member');
      setInviteMessage('');
      setSearchResults([]);
      
      setTimeout(() => {
        fetchMembers();
      }, 1000);
    } catch (error) {
      console.error('发送邀请失败:', error);
      stableMessageRef.current.error(error.message);
    } finally {
      setInviting(false);
    }
  }, [selectedUser, inviteMessage, userId, fetchMembers]);

  // 删除成员
  const handleDeleteMember = useCallback(async (memberUserId) => {
    if (!memberUserId || !userId) return;

    try {
      await teamService.removeMember(userId, memberUserId);
      stableMessageRef.current.success('成员移除成功');
      
      setTimeout(() => {
        fetchMembers();
      }, 1000);
    } catch (error) {
      console.error('删除成员失败:', error);
      stableMessageRef.current.error(error.message || '移除成员失败，请重试');
    }
  }, [userId, fetchMembers]);

  // 撤回邀请
  const handleRevokeInvitation = useCallback(async (invitationId) => {
    if (!invitationId || !userId) return;

    try {
      await teamService.revokeInvitation(userId, invitationId);
      stableMessageRef.current.success('邀请已撤回');
      
      setTimeout(() => {
        fetchMembers();
      }, 1000);
    } catch (error) {
      console.error('撤回邀请失败:', error);
      stableMessageRef.current.error(error.message || '撤回邀请失败，请重试');
    }
  }, [userId, fetchMembers]);

  // tabs 配置
  const tabs = [
    { key: 'members', label: '成员' },
    { key: 'proposals', label: '提案' }
  ];

  // 渲染成员Tab内容
  const renderMembersTab = () => {
    if (membersLoading) {
      return <div className="py-12 text-center text-gray-400">加载中...</div>;
    }
    
    return (
      <div className="space-y-6 pt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isCaptain && (
            <div
              className="flex items-center justify-center bg-white p-6 cursor-pointer border-2 border-dashed border-gray-300 rounded-xl min-h-[120px] hover:border-gray-400 transition-colors duration-200"
              onClick={() => setAddModalVisible(true)}
            >
              <div className="text-center">
                <PlusOutlined className="text-4xl text-gray-400 mb-2" />
                <div className="text-sm text-gray-500">添加成员</div>
              </div>
            </div>
          )}

          {localMembers.length > 0 ? (
            localMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onDelete={handleDeleteMember}
                onRevokeInvitation={handleRevokeInvitation}
                showDelete={isCaptain}
              />
            ))
          ) : !isCaptain ? (
            <div className="col-span-full py-8 text-center text-gray-400">
              暂无成员信息
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // 渲染提案Tab内容
  const renderProposalsTab = () => {
    if (proposalsLoading) {
      return <div className="py-12 text-center text-gray-400">加载中...</div>;
    }
    
    if (!proposals || proposals.length === 0) {
      return (
        <div className="space-y-6 pt-2">
          <EmptyState
            title="暂无提案"
            titleClassName="text-white"
            description="团队还没有关联的提案，可以创建新的提案来丰富团队内容"
          />
        </div>
      );
    }
    
    return (
      <div className="space-y-6 pt-2">
        <div className="flex flex-wrap gap-6">
          {proposals.map((proposal) => {
            const cardProps = mapContentToCard(proposal);
            return (
              <ExploreCard
                key={proposal.id}
                {...cardProps}
                layout="horizontal"
                onClick={() => navigate(`/explore/proposals/${proposal.id}`)}
              />
            );
          })}
        </div>
        
        {proposalsPagination.total_pages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6 text-sm text-gray-500">
            <span>
              第 {proposalsPagination.page} 页，共 {proposalsPagination.total_pages} 页
            </span>
            <span>•</span>
            <span>共 {proposalsPagination.total} 个提案</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="">
      <div className="w-full">
        {/* Tab Navigation */}
        <div className="flex rounded-t-2xl pt-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={
                `text-sm font-medium px-24 py-3 rounded-t-2xl relative ` +
                (activeTab === tab.key
                  ? 'text-white bg-[var(--color-chat-bg)]'
                  : 'text-gray-500')
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="bg-[var(--color-chat-bg)] px-6 pb-6 rounded-b-2xl">
          {activeTab === 'members' && renderMembersTab()}
          {activeTab === 'proposals' && renderProposalsTab()}
        </div>
      </div>

      {/* 邀请成员弹窗 */}
      <Modal
        open={addModalVisible}
        title="邀请成员"
        onCancel={() => {
          setAddModalVisible(false);
          setSelectedUser(null);
          setSearchKeyword('');
          setInviteRole('member');
          setInviteMessage('');
          setSearchResults([]);
        }}
        onOk={handleSendInvitation}
        okText="发送邀请"
        cancelText="取消"
        confirmLoading={inviting}
        width={600}
      >
        <div className="space-y-4">
          {/* 搜索用户 */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              搜索用户 <span className="text-red-500">*</span>
            </div>
            <Input
              placeholder="输入用户名、邮箱或手机号搜索"
              value={searchKeyword}
              onChange={handleSearchChange}
              prefix={<SearchOutlined className="text-gray-400" />}
              className="w-full"
            />
          </div>

          {/* 搜索结果 */}
          {searchResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
              <div className="text-sm text-gray-500 mb-2">搜索结果：</div>
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                    selectedUser?.id === user.id ? 'bg-blue-50 border border-blue-200' : ''
                  }`}
                  onClick={() => handleSelectUser(user)}
                >
                  <UserAvatar src={user.avatar} size={32} user={user} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs text-gray-500">{user.id}</div>
                  </div>
                  {selectedUser?.id === user.id && (
                    <div className="text-blue-500 text-sm">已选择</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 加载状态 */}
          {searchLoading && (
            <div className="flex items-center justify-center py-4">
              <Spin size="small" />
              <span className="ml-2 text-sm text-gray-500">搜索中...</span>
            </div>
          )}

          {/* 邀请消息 */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              邀请消息 <span className="text-red-500">*</span>
            </div>
            <Input.TextArea
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              placeholder="请输入邀请消息，说明邀请原因和期望..."
              rows={3}
              maxLength={50}
              showCount
            />
          </div>

          {/* 提示信息 */}
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            <div>• 搜索关键词至少需要2个字符</div>
            <div>• 邀请消息将发送给被邀请用户</div>
          </div>
        </div>
      </Modal>

      {/* 消息提示容器 */}
      {contextHolder}
    </div>
  );
}
